import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { entAnimations } from 'src/app/animations';
import { MaintenanceDialogComponent } from './maintenance-dialog/maintenance-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { RouteStatus } from 'src/app/models/services/maintenance/routeStatus.model';
import { RouteStatusService } from 'src/app/services/maintenance/route-status.service';

@Component({
  selector: 'app-maintenance',
  templateUrl: './maintenance-page.component.html',
  styleUrl: './maintenance-page.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class MaintenancePageComponent implements OnInit {
  isLoading = false;
  private data: RouteStatus[] = [];
  private initialData: RouteStatus[] = [];
  groupedData: {
    category: string;
    items: RouteStatus[];
    allItemsDisabled: boolean;
  }[] = [];
  isSpecialItemToggled = false;

  constructor(
    private _dialog: MatDialog,
    private _routeStatusService: RouteStatusService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this._routeStatusService.getRouteStatuses().subscribe(data => {
      if (data) {
        this.data = data;
        this.initialData = data.map(item => ({ ...item })); // Clone data for initial state
        this.groupItemsByCategory(data);
        this.isLoading = false;
      } else {
        this.data = [];
      }
      // Check if full maintenance mode to disable all toggles
      this.isSpecialItemToggled = this.data.some(
        item => item.label === 'Full maintenance' && item.isDisabled
      );
    });
  }

  private groupItemsByCategory(data: RouteStatus[]): void {
    const order = [
      'Full maintenance',
      'General',
      'User',
      'Password',
      'Exchange',
    ];

    // Create a new category for "All" and filter the special item
    const allCategoryItems = data.filter(item => item.url === '/*');

    // Filter out the special item from the rest of the data
    const filteredData = data.filter(item => item.url !== '/*');

    // Group the remaining data by category
    const grouped = filteredData.reduce(
      (acc, item) => {
        const categoryIndex = acc.findIndex(
          group => group.category === item.category
        );

        if (categoryIndex === -1) {
          acc.push({
            category: item.category,
            items: [item],
            allItemsDisabled: item.isDisabled, // Initialize allItemsDisabled for the corresponding category
          });
        } else {
          acc[categoryIndex].items.push(item);
          // Update allItemsDisabled if all items in the category are disabled
          acc[categoryIndex].allItemsDisabled = acc[categoryIndex].items.every(
            i => i.isDisabled
          );
        }

        return acc;
      },
      [] as {
        category: string;
        items: RouteStatus[];
        allItemsDisabled: boolean;
      }[] // Include allItemsDisabled in the grouped data
    );

    // Sort the grouped data according to the defined order
    this.groupedData = grouped.sort((a, b) => {
      return order.indexOf(a.category) - order.indexOf(b.category);
    });

    // Add the "All" category with the filtered item at the beginning of the groupedData
    if (allCategoryItems.length > 0) {
      this.groupedData.unshift({
        category: 'Full maintenance',
        items: allCategoryItems,
        allItemsDisabled: allCategoryItems.every(item => item.isDisabled),
      });
    }
  }

  getFirstHalf(items: RouteStatus[]): RouteStatus[] {
    const midIndex = Math.ceil(items.length / 2);
    return items.slice(0, midIndex);
  }

  getSecondHalf(items: RouteStatus[]): RouteStatus[] {
    const midIndex = Math.ceil(items.length / 2);
    return items.slice(midIndex);
  }

  // Method to handle toggling between '/start' and '/validate'
  onToggle(toggledUrl: string) {
    // If '/start' is toggled, also toggle '/validate'
    if (toggledUrl === '/start') {
      const validateItem = this.data.find(item => item.url === '/validation');
      if (validateItem) {
        validateItem.isDisabled = !validateItem.isDisabled;
      }
    }

    // If '/validate' is toggled, also toggle '/start'
    if (toggledUrl === '/validation') {
      const startItem = this.data.find(item => item.url === '/start');
      if (startItem) {
        startItem.isDisabled = !startItem.isDisabled;
      }
    }
  }

  // Method to handle toggling the special item (full maintenance mode)
  onSpecialItemToggle(isToggled: boolean) {
    this.isSpecialItemToggled = isToggled;

    // Toggle all other items
    this.data.forEach(item => {
      item.isDisabled = isToggled;
    });
    // Toggle all category lvl toggles
    this.groupedData.forEach(categoryGroup => {
      categoryGroup.allItemsDisabled = isToggled;
    });
  }

  onCategoryToggle(categoryGroup: {
    category: string;
    items: RouteStatus[];
    allItemsDisabled: boolean;
  }): void {
    categoryGroup.items.forEach(item => {
      item.isDisabled = categoryGroup.allItemsDisabled; // Set each item status to match the category's toggle state
    });
  }

  // Dialog confirm maintenance
  openDialog(): void {
    let changedItems = this.data.filter(
      (item, index) => item.isDisabled !== this.initialData[index].isDisabled
    );
    if (changedItems.some(item => item.url === '/*')) {
      changedItems = this.initialData.map(item => {
        return {
          ...item,
          isDisabled: changedItems.some(ci => ci.url === '/*' && ci.isDisabled),
        };
      });
    }
    this._dialog.open(MaintenanceDialogComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: 'none',
      maxHeight: 'none',
      data: { changedItems: changedItems },
      panelClass: 'custom-dialog-container',
    });
  }

  refresh(): void {
    this.groupedData.forEach(categoryGroup => {
      categoryGroup.items.forEach(item => {
        item.isDisabled = false;
      });
    });
    this.groupedData.forEach(categoryGroup => {
      categoryGroup.allItemsDisabled = false;
    });
    this.isSpecialItemToggled = false; // Reset the special item toggled state as well
  }
}
